/**
 * Spanish Legal Translations
 * 法律文档翻译模块 - 西班牙语
 * 
 * 包含隐私政策 (privacy) 和服务条款 (terms) 等法律文档的翻译
 */

import type { LegalTranslations } from '../../types';

export const esLegal: LegalTranslations = {
  privacy: {
    title: 'Política de Privacidad',
    subtitle: 'Cómo recopilamos, usamos y protegemos su información personal.',
    lastUpdated: 'Última Actualización',
    effectiveDate: 'Fecha de Entrada en Vigor',
    meta: {
      lastUpdatedValue: '6 de febrero de 2026',
      effectiveDateValue: '6 de febrero de 2026',
    },
    introduction: 'Fujian Jinghui Environmental Technology Co., Ltd. ("nosotros", "nuestro" o "nosotros") se compromete a proteger su privacidad. Esta Política de Privacidad explica cómo recopilamos, usamos, divulgamos y protegemos su información cuando visita nuestro sitio web y utiliza nuestros servicios.',
    sections: {
      infoCollect: {
        title: '1. Información Que Recopilamos',
        description: 'Podemos recopilar información personal que usted nos proporciona voluntariamente cuando:',
        items: [
          'Se registra en nuestro sitio web o realiza una consulta',
          'Solicita una cotización o información del producto',
          'Se suscribe a nuestro boletín',
          'Contacta a nuestro equipo de ventas o soporte',
          'Participa en encuestas o promociones',
        ],
      },
      typesCollected: {
        title: '2. Tipos de Información Recopilada',
        items: [
          'Información de Identificación Personal: Nombre, correo electrónico, número de teléfono, nombre de la empresa, puesto',
          'Información Demográfica: País, región, preferencias de idioma',
          'Información Técnica: Dirección IP, tipo de navegador, sistema operativo, URLs de referencia',
          'Datos de Comunicación: Mensajes, consultas, comentarios y solicitudes de soporte',
        ],
      },
      howUse: {
        title: '3. Cómo Usamos Su Información',
        description: 'Usamos la información que recopilamos para:',
        items: [
          'Proporcionar, mantener y mejorar nuestros productos y servicios',
          'Responder a sus consultas y proporcionar soporte al cliente',
          'Enviarle información del producto, cotizaciones y materiales promocionales',
          'Procesar transacciones y enviar información relacionada',
          'Monitorear y analizar tendencias, uso y actividades',
          'Detectar, investigar y prevenir transacciones fraudulentas',
          'Cumplir con obligaciones legales',
        ],
      },
      dataSecurity: {
        title: '4. Seguridad de Datos',
        description: 'Implementamos medidas de seguridad técnicas y organizacionales apropiadas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción. Sin embargo, ningún método de transmisión por Internet o almacenamiento electrónico es 100% seguro.',
        encryption: 'Usamos encriptación estándar de la industria (SSL/TLS) para proteger los datos transmitidos entre su navegador y nuestros servidores.',
      },
      cookies: {
        title: '5. Cookies y Tecnologías de Seguimiento',
        description: 'Nuestro sitio web puede usar cookies, web beacons y tecnologías de seguimiento similares para recopilar información sobre sus actividades de navegación. Puede configurar su navegador para rechazar todas las cookies o para indicar cuándo se envía una cookie.',
      },
      thirdParty: {
        title: '6. Divulgación a Terceros',
        description: 'No vendemos, intercambiamos ni transferimos su información de identificación personal a terceros excepto en las siguientes circunstancias:',
        items: [
          'Con proveedores de servicios que asisten en nuestras operaciones comerciales',
          'Cuando lo requiera la ley, citación u otro proceso legal',
          'Para proteger nuestros derechos, privacidad, seguridad o propiedad',
          'En conexión con una fusión, adquisición o venta de activos',
        ],
      },
      dataRetention: {
        title: '7. Retención de Datos',
        description: 'Retendremos su información personal durante el tiempo que sea necesario para cumplir con los propósitos descritos en esta Política de Privacidad, a menos que la ley requiera o permita un período de retención más largo.',
      },
      yourRights: {
        title: '8. Sus Derechos',
        description: 'Tiene derecho a:',
        items: [
          'Acceder y recibir una copia de sus datos personales',
          'Rectificar datos personales inexactos',
          'Solicitar la eliminación de sus datos personales',
          'Objetar al procesamiento de sus datos personales',
          'Solicitar la restricción del procesamiento',
          'Portabilidad de datos',
        ],
      },
      children: {
        title: '9. Privacidad de Menores',
        description: 'Nuestro sitio web no está dirigido a niños menores de 16 años. No recopilamos intencionalmente información personal de niños menores de 16 años.',
      },
      international: {
        title: '10. Transferencias Internacionales de Datos',
        description: 'Su información puede ser transferida y procesada en países distintos a su país de residencia. Estos países pueden tener leyes de protección de datos diferentes a las de su país.',
      },
      changes: {
        title: '11. Cambios a Esta Política',
        description: 'Podemos actualizar esta Política de Privacidad de vez en cuando. Le notificaremos cualquier cambio publicando la nueva Política de Privacidad en esta página y actualizando la fecha de "Última Actualización" en la parte superior de esta política.',
      },
      contactUs: {
        title: '12. Contáctenos',
        description: 'Si tiene alguna pregunta sobre esta Política de Privacidad o nuestras prácticas de datos, contáctenos:',
        email: 'Correo Electrónico',
        address: 'Dirección',
        company: 'Fujian Jinghui Environmental Technology Co., Ltd.',
        addressLine: 'Edificio 25, Parque Científico y Tecnológico Wanyang, 26 Calle Tingwang, Pueblo Tingjiang, Distrito Mawei, Fuzhou, Fujian, China',
      },
    },
  },
  terms: {
    title: 'Términos de Servicio',
    subtitle: 'Lea estos términos cuidadosamente antes de usar nuestro sitio web y servicios.',
    lastUpdated: 'Última Actualización',
    effectiveDate: 'Fecha de Entrada en Vigor',
    meta: {
      lastUpdatedValue: '6 de febrero de 2026',
      effectiveDateValue: '6 de febrero de 2026',
    },
    introduction: 'Bienvenido al sitio web de Fujian Jinghui Environmental Technology Co., Ltd. Al acceder o usar este sitio web, acepta estar obligado por estos Términos de Servicio y todas las leyes y regulaciones aplicables. Si no está de acuerdo con alguna parte de estos términos, no puede usar nuestro sitio web.',
    importantNotice: {
      title: 'Aviso Importante',
      description: 'Al usar este sitio web, reconoce que ha leído, entendido y aceptado estar obligado por estos Términos de Servicio. Si está usando este sitio web en nombre de una compañía u organización, representa que tiene la autoridad para obligar a dicha entidad a estos términos.',
    },
    sections: {
      useWebsite: {
        title: '1. Uso del Sitio Web',
        description: 'Este sitio web es proporcionado por Fujian Jinghui Environmental Technology Co., Ltd. solo para fines informativos. Al usar este sitio web, acepta:',
        items: [
          'Usar el sitio web solo para fines legales y de acuerdo con estos Términos',
          'No usar el sitio web de ninguna manera que pueda dañarlo, deshabilitarlo, sobrecargarlo o deteriorarlo',
          'No intentar obtener acceso no autorizado a ninguna parte del sitio web',
          'No usar el sitio web para transmitir virus, gusanos o código dañino',
          'Respetar los derechos de propiedad intelectual de JH Heatsup y otros',
        ],
      },
      intellectualProperty: {
        title: '2. Derechos de Propiedad Intelectual',
        description: 'Todo el contenido de este sitio web, incluyendo pero no limitado a texto, gráficos, logotipos, imágenes, clips de audio, descargas digitales y software, es propiedad de Fujian Jinghui Environmental Technology Co., Ltd. o sus proveedores de contenido y está protegido por leyes internacionales de derechos de autor, marcas comerciales y otras leyes de propiedad intelectual.',
        restriction: 'No puede reproducir, distribuir, modificar, crear obras derivadas, exhibir públicamente, ejecutar públicamente o explotar ningún contenido de este sitio web sin nuestro consentimiento previo por escrito.',
      },
      productInfo: {
        title: '3. Información del Producto y Precios',
        description: 'Todos los productos y servicios presentados en este sitio web están sujetos a disponibilidad. Nos reservamos el derecho de discontinuar cualquier producto o servicio en cualquier momento.',
        pricing: 'Los precios y especificaciones están sujetos a cambios sin previo aviso. hacemos todo lo posible para garantizar la exactitud de la información del producto, pero pueden ocurrir errores. Nos reservamos el derecho de corregir cualquier error, inexactitud u omisión y de cambiar o actualizar información en cualquier momento.',
        currency: 'Todos los precios están cotizados en USD a menos que se especifique lo contrario. Los costos de envío, impuestos y aranceles son adicionales y se calcularán durante el proceso de pedido.',
      },
      quotesOrders: {
        title: '4. Cotizaciones y Pedidos',
        description: 'Las cotizaciones proporcionadas a través de este sitio web son válidas por 30 días a menos que se especifique lo contrario. Todos los pedidos están sujetos a aceptación por JH Heatsup.',
        contract: 'Al enviar un pedido, acepta comprar los productos de acuerdo con nuestros términos vigentes en ese momento, incluyendo precios, envío y términos de pago. Un contrato vinculante se forma solo cuando confirmamos la aceptación de su pedido.',
      },
      warranties: {
        title: '5. Garantías y Descargos de Responsabilidad',
        description: 'ESTE SITIO WEB Y TODO EL CONTENIDO, PRODUCTOS Y SERVICIOS PROPORCIONADOS A TRAVÉS DE ÉL SE PROPORCIONAN "TAL CUAL" Y "COMO ESTÁN DISPONIBLES" SIN GARANTÍAS DE NINGÚN TIPO, YA SEAN EXPRESAS O IMPLÍCITAS.',
        disclaimer: 'JH Heatsup RECHAZA TODAS LAS GARANTÍAS, EXPRESAS O IMPLÍCITAS, INCLUYENDO PERO NO LIMITADO A GARANTÍAS IMPLÍCITAS DE COMERCIABILIDAD, IDONEIDAD PARA UN PROPÓSITO PARTICULAR, TÍTULO Y NO INFRACCIÓN. No garantizamos que el sitio web estará ininterrumpido, oportuno, seguro o sin errores.',
      },
      liability: {
        title: '6. Limitación de Responsabilidad',
        description: 'EN NINGÚN CASO FUJIAN JINGHUI ENVIRONMENT TECHNOLOGY CO., LTD., SUS FUNCIONARIOS, DIRECTORES, EMPLEADOS O AGENTES SERÁN RESPONSABLES DE CUALQUIER DAÑO INDIRECTO, INCIDENTAL, ESPECIAL, CONSECUENCIAL O PUNITIVO QUE SURJA DE O ESTÉ RELACIONADO CON SU USO DE ESTE SITIO WEB O CUALQUIER PRODUCTO O SERVICIO COMPRADO DE NOSOTROS.',
        limit: 'Nuestra responsabilidad total por cualquier reclamo que surja de o esté relacionado con estos términos o nuestros productos y servicios no excederá la cantidad que usted pagará por los productos o servicios que dieron lugar a dicho reclamo.',
      },
      privacy: {
        title: '7. Política de Privacidad',
        description: 'Su uso de este sitio web también se rige por nuestra Política de Privacidad, que se incorpora a estos Términos por referencia. Revise nuestra Política de Privacidad para entender cómo recopilamos, usamos y protegemos su información.',
      },
      thirdPartyLinks: {
        title: '8. Enlaces de Terceros',
        description: 'Este sitio web puede contener enlaces a sitios web de terceros que no son propiedad ni están controlados por JH Heatsup. No tenemos control sobre, y no asumimos responsabilidad por, el contenido, políticas de privacidad o prácticas de ningún sitio web de terceros. Accede a dichos enlaces bajo su propio riesgo.',
        recommendation: 'Le recomendamos que revise los términos y la política de privacidad de cualquier sitio web de terceros que visite.',
      },
      governingLaw: {
        title: '9. Ley Aplicable y Disputas',
        description: 'Estos Términos se regirán e interpretarán de acuerdo con las leyes de la República Popular China, sin regard a sus disposiciones de conflicto de leyes.',
        disputes: 'Cualquier disputa que surja de o esté relacionada con estos Términos o su uso de este sitio web se resolverá mediante negociaciones de buena fe entre las partes. Si dichas negociaciones fallan, la disputa se someterá a arbitraje vinculante de acuerdo con las reglas de la Comisión China de Arbitraje Económico y Comercial Internacional.',
      },
      export: {
        title: '10. Controles de Exportación',
        description: 'Acepta cumplir con todas las leyes y regulaciones de control de exportación aplicables. Representa que no se encuentra ubicado bajo el control de, o es nacional o residente de, cualquier país sujeto a sanciones o embargos completos de EE. UU.',
      },
      termination: {
        title: '11. Terminación',
        description: 'Podemos terminar o suspender su acceso a este sitio web inmediatamente, sin previo aviso ni responsabilidad, por cualquier motivo, incluyendo incumplimiento de estos Términos. Upon termination, su derecho a usar el sitio web cesará inmediatamente.',
      },
      changes: {
        title: '12. Cambios a Estos Términos',
        description: 'Nos reservamos el derecho de modificar o reemplazar estos Términos en cualquier momento. Si una revisión es material, proporcionaremos un aviso 30 días antes de que nuevos términos entren en vigencia. Su uso continuo del sitio web después de dichos cambios constituye aceptación de los nuevos Términos.',
      },
      severability: {
        title: '13. Divisibilidad',
        description: 'Si alguna disposición de estos Términos se considera inválida, ilegal o inejecutable, dicha disposición se modificará al mínimo necesario para hacerla ejecutable, y las disposiciones restantes continuarán en pleno vigor y efecto.',
      },
      contactUs: {
        title: '14. Información de Contacto',
        description: 'Si tiene alguna pregunta sobre estos Términos de Servicio, contáctenos:',
        email: 'Correo Electrónico',
        address: 'Dirección',
        company: 'Fujian Jinghui Environmental Technology Co., Ltd.',
        addressLine: 'Edificio 25, Parque Científico y Tecnológico Wanyang, 26 Calle Tingwang, Pueblo Tingjiang, Distrito Mawei, Fuzhou, Fujian, China',
      },
    },
  },
  installationGuide: {
    header: {
      title: 'Guía de Instalación',
      subtitle: 'Siga estos pasos para una instalación segura y eficiente de su calefactor JH Heatsup.',
    },
    safety: {
      title: 'Seguridad Primero',
      description:
        'La instalación debe ser realizada por un electricista calificado de acuerdo con los códigos eléctricos locales. Asegúrese siempre de que el suministro eléctrico esté cortado en el panel principal antes de comenzar la instalación.',
    },
    toolsTitle: 'Herramientas Necesarias',
    tools: {
      electricDrill: 'Taladro Eléctrico',
      screwdriver: 'Destornillador',
      level: 'Nivel',
      measuringTape: 'Cinta Métrica',
      wireStrippers: 'Pelacables',
      ladder: 'Escalera',
    },
    stepsTitle: 'Pasos de Instalación',
    steps: {
      step1: {
        title: 'Posicionamiento y Medición',
        description:
          'Elija una ubicación en el techo o pared alta. Para un rendimiento óptimo, el calefactor debe instalarse a una altura de 2,2 m a 2,5 m del suelo. Marque los puntos de perforación según las dimensiones del soporte de montaje incluido en la caja.',
        bullet1: 'Mantenga una distancia mínima de 0,5 m de las paredes laterales.',
        bullet2: 'Asegure una distancia mínima de 0,2 m del techo (si se monta en pared).',
      },
      step2: {
        title: 'Montaje de los Soportes',
        description:
          'Perfore agujeros en las posiciones marcadas e inserte los tornillos de expansión. Fije los soportes de montaje de forma segura a la superficie. Deslice los soportes deslizantes traseros del calefactor en los soportes montados y apriete los tornillos de ajuste para establecer el ángulo deseado (0°, 15°, 30° o 45°).',
      },
      step3: {
        title: 'Conexión Eléctrica',
        description:
          'Conecte el cable de alimentación al suministro eléctrico principal. Asegure una correcta puesta a tierra. Para unidades con control remoto, asegúrese de que el ojo receptor sea visible para que el control remoto funcione eficazmente.',
        wireLive: 'L — Fase',
        wireLiveColor: 'Marrón',
        wireNeutral: 'N — Neutro',
        wireNeutralColor: 'Azul',
        wireEarth: 'E — Tierra',
        wireEarthColor: 'Amarillo / Verde',
      },
      step4: {
        title: 'Prueba',
        description:
          'Encienda la alimentación. El calefactor debe alcanzar la temperatura de funcionamiento completa en 5 minutos. Puede notar un ligero olor durante el primer uso; esto es normal y se disipará rápidamente.',
      },
    },
    download: {
      title: 'Ver Manual del Producto',
      subtitle: 'Consulte el manual completo de instalación con diagramas para su modelo específico.',
      jhnr: 'Ver Manual',
      industrial: 'Ver Manual',
    },
  },
  warrantyPolicy: {
    header: {
      title: 'Política de Garantía',
      subtitle: 'Nuestro compromiso con la calidad y su tranquilidad.',
    },
    overview: {
      standardCoverage: 'Cobertura Estándar',
      standardCoverageDesc:
        'Los productos JH Heatsup están garantizados contra defectos de materiales y mano de obra bajo uso normal.',
      warrantyPeriod: 'Período de Garantía',
      residentialPeriod: '2 Años para calefactores residenciales.',
      industrialPeriod: '1 Año para calefactores industriales y controladores.',
    },
    sections: {
      scope: {
        title: '1. Alcance de la Garantía',
        description:
          'Esta garantía se aplica al comprador original del producto y no es transferible. Cubre la reparación o reemplazo de cualquier parte del producto que resulte defectuosa en material o mano de obra dentro del período de garantía.',
      },
      notCovered: {
        title: '2. ¿Qué No Está Cubierto?',
        description: 'Esta garantía no cubre daños resultantes de:',
        item1: 'Instalación incorrecta que no se ajuste al manual proporcionado.',
        item2: 'Mal uso, abuso, negligencia o accidente.',
        item3: 'Alteración o modificación del producto.',
        item4: 'Sobretensiones o rayos.',
        item5: 'Desgaste normal (por ejemplo, decoloración de la superficie con el tiempo).',
      },
      claim: {
        title: '3. Cómo Realizar una Reclamación',
        description: 'Para obtener servicio de garantía, comuníquese con nuestro equipo de soporte con la siguiente información:',
        proofOfPurchase: 'Comprobante de compra (Factura/Recibo)',
        defectDescription: 'Descripción y fotos/video del defecto',
        serialNumber: 'Número de serie de la unidad',
        contactEmail: 'Correo electrónico de contacto',
      },
    },
  },
};
